import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';

const DISMISS_KEY = 'verifaeye_install_dismissed_at';
const DISMISS_DAYS = 30;

const dismissedRecently = () => {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  return Date.now() - Number(raw) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIosSafari = () => {
  const ua = window.navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
};

/** Invites people to keep VerifAEye on their home screen. Chrome/Android gets the real
 *  install prompt; iOS Safari gets the Share → Add to Home Screen instruction. */
export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    let timer;
    if (isIosSafari()) {
      timer = setTimeout(() => setShowIosHint(true), 4000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferred(null);
    setShowIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    dismiss();
  };

  if (!deferred && !showIosHint) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] z-40 animate-slide-up rounded-xl border border-slate-700 bg-slate-900 p-3 shadow-2xl sm:inset-x-auto sm:right-4 sm:max-w-sm lg:bottom-6"
      data-testid="install-prompt"
    >
      <div className="flex items-start gap-3">
        <picture>
          <source srcSet="/logo-mark.webp" type="image/webp" />
          <img src="/logo-mark.png" width="96" height="96" alt="" aria-hidden="true" className="h-10 w-10 shrink-0 rounded-lg" />
        </picture>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">Keep VerifAEye on your home screen</p>
          {deferred ? (
            <p className="mt-0.5 text-xs leading-relaxed text-slate-400">
              Opens full screen and is always one tap away when you need to photograph something.
            </p>
          ) : (
            <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs leading-relaxed text-slate-400">
              Tap <Share className="inline h-3.5 w-3.5 text-slate-300" /> then
              <span className="text-slate-300">Add to Home Screen</span>.
            </p>
          )}
          {deferred && (
            <button
              type="button"
              onClick={install}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-500"
              data-testid="install-accept-btn"
            >
              <Download className="h-3.5 w-3.5" />
              Add to home screen
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="-m-1 p-1 text-slate-500 transition-colors hover:text-slate-300"
          data-testid="install-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
